import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, ScrollView, TouchableOpacity, Pressable } from "react-native";
import { Button, Card, Text } from "react-native-paper";
import * as DocumentPicker from "expo-document-picker";
import { useFocusEffect } from "@react-navigation/native";
import { View } from "../../components/shared/Themed";
import Spinner from "../../components/shared/Spinner";
import useColorScheme from "../../hooks/useColorScheme";
import { CustomHorizontalLine } from "../../components/custom/CustomHorizontalLine";

import * as XLSX from "xlsx";
import {
  containerMongoDbModal,
  containerSpreadSheetModal,
} from "../../models/containersModel";
import { SelectBoxControl } from "../../components/custom/SelectBoxControl";
import { TextInputControl } from "../../components/custom/TextInputControl";
import { DateInputControl } from "../../components/custom/DateInputControl";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";

import { MaterialCommunityIcons, Feather } from "@expo/vector-icons";
import { addDaysToDate } from "../../utils/dateUtility";

import { useForm, Controller } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import {
  manageContainer,
  getContainerById,
  manageBulkContainers,
} from "../../services/containersService";
import { containersState } from "../../redux/state/containers";
import { ReadOnlyProps } from "../../utils/templates";
import { useSelector } from "react-redux";
import oktaConfig from "../../configs/okta";

interface Props {
  route: any;
  navigation: any;
}

export default function ManageContainers({
  route,
  navigation,
}: ReadOnlyProps<Props>) {
  const params = route.params;
  const { userRoles } = useSelector((state: any) => state.configurations);

  const isContainerUpdated: boolean =
    params.container && Object.keys(params.container).length > 0;
  const containerRecord: containerMongoDbModal = isContainerUpdated
    ? JSON.parse(params.container)
    : {};

  const toast = useToast();
  const colorScheme = useColorScheme();

  const isUserAdmin = userRoles.some((role: string) =>
    containersState.allowedRolesForContainers.includes(role)
  );

  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const scrollRef = React.useRef<ScrollView>(null);

  useFocusEffect(
    React.useCallback(() => {
      // Small delay to ensure layout is ready if needed, 
      // but usually scrollTo({y:0}) on focus is enough
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    }, [])
  );
  const [displayLoader, setDisplayLoader] = useState<boolean>(false);
  const [displayBulkLoader, setDisplayBulkLoader] = useState<boolean>(false);
  const [activePicker, setActivePicker] = useState<string | null>(null);

  const ExcelDateToJSDate = (date: number) => {
    return new Date(Math.round((date - 25569) * 86400 * 1000));
  };

  const ProcessFile = async () => {
    const selectedFileResult = await DocumentPicker.getDocumentAsync({
      type: "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const containers: Array<any> = [];
    if (
      selectedFileResult.canceled === false &&
      selectedFileResult.assets &&
      selectedFileResult.assets.length > 0
    ) {
      const selectedFile = selectedFileResult.assets[0];
      setDisplayBulkLoader(true);
      const selectedFile_ArrayBuffer = await fetch(selectedFile.uri);
      const workbook = XLSX.read(await selectedFile_ArrayBuffer.arrayBuffer(), {
        type: "buffer",
      });
      const Sheet = workbook.Sheets["Final"];
      const SheetRows =
        XLSX.utils.sheet_to_json<containerSpreadSheetModal>(Sheet);

      SheetRows.forEach((row) => {
        const etaStore = row["ETA(Store)"] as string;
        const eta_store_end_date =
          etaStore.split("-")[1] !== undefined
            ? (etaStore.split("-")[1] as string).split("/")
            : "";
        const eta_store_end_date_format =
          eta_store_end_date !== ""
            ? `${eta_store_end_date[1]}/${eta_store_end_date[0]}/${eta_store_end_date[2]}`
            : "";

        containers.push({
          coo_number: row["Certificate of Import"],
          status: row.Type,
          container_number: row["Container No."],
          deliver_to: row["Ship Via"],
          etd: ExcelDateToJSDate(row.ETD as number),
          eta_port: ExcelDateToJSDate(row["ETA(Port)"] as number),
          eta_store:
            eta_store_end_date_format !== ""
              ? new Date(eta_store_end_date_format)
              : null,
          country_state: row.State,
          priority: row["Priority Level"],
        });
      });
      const result = await manageBulkContainers(containers);
      toast.show(result.data.message, {
        type: result.isSuccessfull ? "success" : "danger",
      });
      if (result.isSuccessfull) {
        backToList();
      }
    } else {
      toast.show("Error: couldn't retrieve the file", {
        type: "danger",
      });
    }

    setDisplayBulkLoader(false);
  };

  const backToList = () => {
    resetForm();
    setTimeout(() => {
      window.location.href = `${oktaConfig.api.redirectUri}/containers/list`;
    }, 1000);
  };

  const containerDefaultValues = {
    importer: "",
    type: "",
    size: "",
    container_number: "",
    deliver_to: "",
    country_state: "",
    etd: new Date(),
    eta_port: addDaysToDate(new Date(), 14),
    eta_store: addDaysToDate(new Date(), 29),
    updated_eta: new Date(),
    dd_number: "",
    coo_number: "",
    status: "",
    remark: "",
    delay_reason: "",
    vessal_detail: "",
    priority: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: containerDefaultValues,
  });

  const resetForm = () => {
    reset(containerDefaultValues);
  };

  useEffect(() => {
    if (isContainerUpdated) {
      setValue(
        "importer",
        containerRecord.importer ? (containerRecord.importer as string) : ""
      );
      setValue(
        "type",
        containerRecord.type ? (containerRecord.type as string) : ""
      );
      setValue(
        "size",
        containerRecord.size ? (containerRecord.size as string) : ""
      );
      setValue(
        "container_number",
        containerRecord.container_number ? containerRecord.container_number : ""
      );
      setValue(
        "deliver_to",
        containerRecord.deliver_to ? containerRecord.deliver_to : ""
      );
      setValue(
        "country_state",
        containerRecord.country_state
          ? (containerRecord.country_state as string)
          : ""
      );
      setValue(
        "etd",
        containerRecord.etd ? new Date(containerRecord.etd) : new Date()
      );
      setValue(
        "eta_port",
        containerRecord.eta_port
          ? new Date(containerRecord.eta_port)
          : addDaysToDate(new Date(), 14)
      );
      setValue(
        "eta_store",
        containerRecord.eta_store
          ? new Date(containerRecord.eta_store)
          : addDaysToDate(new Date(), 29)
      );
      setValue(
        "updated_eta",
        containerRecord.updated_eta
          ? new Date(containerRecord.updated_eta)
          : new Date()
      );
      setValue(
        "dd_number",
        containerRecord.dd_number ? containerRecord.dd_number : ""
      );
      setValue(
        "coo_number",
        containerRecord.coo_number ? containerRecord.coo_number : ""
      );
      setValue(
        "status",
        containerRecord.status ? (containerRecord.status as string) : ""
      );
      setValue("remark", containerRecord.remark ? containerRecord.remark : "");
      setValue(
        "delay_reason",
        containerRecord.delay_reason ? containerRecord.delay_reason : ""
      );
      setValue(
        "vessal_detail",
        containerRecord.vessal_detail ? containerRecord.vessal_detail : ""
      );
      setValue(
        "priority",
        containerRecord.priority ? (containerRecord.priority as string) : ""
      );

      //Set displayLoader to false
      setDisplayLoader(false);
    }
  }, [route.params]);

  const isContainerExists = (containerNumber: string = "") => {
    return getContainerById(containerNumber)
      .then((result) => {
        return Object.keys(result.data).length > 0;
      })
      .catch(() => {
        return false;
      });
  };

  const onSubmit = async (formData: any) => {
    setDisplayLoader(!displayLoader);

    const transactionType = params.mode === "create" ? "add" : "update";
    const containerForm =
      params.mode === "create"
        ? formData
        : {
          _id: containerRecord._id,
          ...formData,
        };

    await manageContainer(containerForm, transactionType)
      .then((result) => {
        toast.show(result.data.message, {
          type: "success",
        });

        backToList();
      })
      .catch((err) => {
        toast.show(err.message, {
          type: "danger",
        });
      })
      .finally(() => {
        setDisplayLoader(!displayLoader);
      });
  };

  return (
    <ScrollView ref={scrollRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent}>

      {/* ── Top Bar ── */}
      <View style={styles.topBar}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <MaterialCommunityIcons name="arrow-left" size={20} color="#64748B" />
        </TouchableOpacity>
        <View style={styles.topBarCenter}>
          <Text style={styles.topBarTitle}>
            {params.mode === "create" ? "Create Container" : "Edit Container"}
          </Text>
          <Text style={styles.topBarSubtitle}>
            {params.mode === "create" ? "Add a new container to the system" : "Modify existing container details"}
          </Text>
        </View>
      </View>

      <Pressable onPress={() => setActivePicker(null)} style={styles.container}>

        {/* ── Upload section (create mode only) ── */}
        {params.mode === "create" && (
          <View style={styles.section}>
            <TouchableOpacity
              onPress={ProcessFile}
              disabled={displayBulkLoader}
              style={styles.uploadBox}
            >
              {displayBulkLoader ? (
                <Spinner size="small" color="#2F95DC" />
              ) : (
                <>
                  <MaterialCommunityIcons name="file-upload-outline" size={28} color="#2F95DC" />
                  <Text style={styles.uploadTitle}>UPLOAD CONTAINERS</Text>
                  <Text style={styles.uploadSub}>Only .XLSX file allowed</Text>
                </>
              )}
            </TouchableOpacity>

            <View style={styles.dividerRow}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>OR MANUAL ENTRY</Text>
              <View style={styles.dividerLine} />
            </View>
          </View>
        )}

        {/* ── Form Card ── */}
        <View style={styles.card}>

          {/* Section: Container Identity */}
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="package-variant-closed" size={14} color="#2F95DC" />
            <Text style={styles.sectionTitle}>CONTAINER IDENTITY</Text>
          </View>

          <Controller
            control={control}
            rules={{ required: false }}
            name="importer"
            render={({ field: { onChange, value } }) => (
              <SelectBoxControl
                Items={containersState.importerList}
                defaultOption={value}
                title="Importer"
                width="100%"
                placeholder="Select Importer"
                required={false}
                onSelectedValue={onChange}
                open={activePicker === "importer"}
                setOpen={(val: any) => setActivePicker(val ? "importer" : null)}
              />
            )}
          />
          {errors.importer && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: true }}
            name="type"
            render={({ field: { onChange, value } }) => (
              <SelectBoxControl
                Items={containersState.modalityTypeList}
                defaultOption={value}
                title="Type"
                width="100%"
                placeholder="Select Type"
                required={true}
                onSelectedValue={onChange}
                open={activePicker === "type"}
                setOpen={(val: any) => setActivePicker(val ? "type" : null)}
              />
            )}
          />
          {errors.type && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: false }}
            name="size"
            render={({ field: { onChange, value } }) => (
              <SelectBoxControl
                Items={containersState.containerSizeList}
                defaultOption={value}
                title="Size"
                width="100%"
                placeholder="Select Size"
                required={false}
                onSelectedValue={onChange}
                open={activePicker === "size"}
                setOpen={(val: any) => setActivePicker(val ? "size" : null)}
              />
            )}
          />
          {errors.size && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{
              validate: {
                required: async (value) => {
                  if (!value) return "* This is required.";
                  if (params.mode === "create") {
                    if (value && (await isContainerExists(value))) {
                      return "* Container Number already exists.";
                    }
                  }
                },
              },
            }}
            name="container_number"
            render={({ field: { onChange, value } }) => (
              <TextInputControl
                title="Container Number"
                required={true}
                width="100%"
                placeholder="Enter Container Number"
                value={value}
                setChangeText={onChange}
                editable={params.mode === "update" ? false : true}
              />
            )}
          />
          {errors.container_number && (
            <Text style={errors.container_number.message === "* Container Number is available." ? styles.successColor : styles.errorColor}>
              {errors.container_number.message}
            </Text>
          )}

          <View style={styles.cardDivider} />

          {/* Section: Status & Location */}
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="map-marker-outline" size={14} color="#2F95DC" />
            <Text style={styles.sectionTitle}>STATUS & LOCATION</Text>
          </View>

          <Controller
            control={control}
            rules={{ required: true }}
            name="deliver_to"
            render={({ field: { onChange, value } }) => (
              <TextInputControl
                title="Delivery Location"
                required={true}
                width="100%"
                placeholder="Enter Delivery Location"
                value={value}
                setChangeText={onChange}
              />
            )}
          />
          {errors.deliver_to && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: true }}
            name="country_state"
            render={({ field: { onChange, value } }) => (
              <SelectBoxControl
                Items={containersState.countryStateList}
                title="State"
                width="100%"
                placeholder="Select State"
                required={true}
                defaultOption={value}
                onSelectedValue={onChange}
                open={activePicker === "country_state"}
                setOpen={(val: any) => setActivePicker(val ? "country_state" : null)}
              />
            )}
          />
          {errors.country_state && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: false }}
            name="status"
            render={({ field: { onChange, value } }) => (
              <SelectBoxControl
                Items={containersState.shipmentStatusList}
                defaultOption={value}
                title="Status"
                width="100%"
                placeholder="Select Status"
                required={false}
                onSelectedValue={onChange}
                open={activePicker === "status"}
                setOpen={(val: any) => setActivePicker(val ? "status" : null)}
              />
            )}
          />
          {errors.status && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: true }}
            name="priority"
            render={({ field: { onChange, value } }) => (
              <SelectBoxControl
                Items={containersState.priorityList}
                defaultOption={value}
                title="Priority"
                width="100%"
                placeholder="Select Priority"
                required={true}
                onSelectedValue={onChange}
                open={activePicker === "priority"}
                setOpen={(val: any) => setActivePicker(val ? "priority" : null)}
              />
            )}
          />
          {errors.priority && <Text style={styles.errorColor}>* This is required.</Text>}

          <View style={styles.cardDivider} />

          {/* Section: Dates */}
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="calendar-range" size={14} color="#2F95DC" />
            <Text style={styles.sectionTitle}>DATES & TIMING</Text>
          </View>

          <Controller
            control={control}
            rules={{ required: true }}
            name="etd"
            render={({ field: { onChange, value } }) => (
              <DateInputControl
                title="ETD"
                required={true}
                setDate={onChange}
                value={value}
                width="100%"
                defaultText="Select ETD"
                disabled={true}
              />
            )}
          />
          {errors.etd && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: true }}
            name="eta_port"
            render={({ field: { onChange, value } }) => (
              <DateInputControl
                title="ETA"
                required={true}
                setDate={onChange}
                value={value}
                width="100%"
                defaultText="Select ETA"
                disabled={true}
              />
            )}
          />
          {errors.eta_port && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: true }}
            name="eta_store"
            render={({ field: { onChange, value } }) => (
              <DateInputControl
                title="ETA to Store"
                required={true}
                setDate={onChange}
                value={value}
                width="100%"
                defaultText="Select ETA to Store"
                disabled={true}
              />
            )}
          />
          {errors.eta_store && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: false }}
            name="updated_eta"
            render={({ field: { onChange, value } }) => (
              <DateInputControl
                title="Updated ETA"
                required={false}
                setDate={onChange}
                value={value}
                width="100%"
                defaultText="Select Updated ETA"
                disabled={true}
              />
            )}
          />
          {errors.updated_eta && <Text style={styles.errorColor}>* This is required.</Text>}

          <View style={styles.cardDivider} />

          {/* Section: Additional Details */}
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="text-box-outline" size={14} color="#2F95DC" />
            <Text style={styles.sectionTitle}>ADDITIONAL DETAILS</Text>
          </View>

          <Controller
            control={control}
            rules={{ required: false }}
            name="dd_number"
            render={({ field: { onChange, value } }) => (
              <TextInputControl
                title="Packing List #"
                required={false}
                width="100%"
                placeholder="Enter Packing List #"
                value={value}
                setChangeText={onChange}
              />
            )}
          />
          {errors.dd_number && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: true }}
            name="coo_number"
            render={({ field: { onChange, value } }) => (
              <TextInputControl
                title="Certificate of Origin (COO) #"
                required={true}
                width="100%"
                placeholder="Enter COO #"
                value={value}
                setChangeText={onChange}
              />
            )}
          />
          {errors.coo_number && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: false }}
            name="remark"
            render={({ field: { onChange, value } }) => (
              <TextInputControl
                title="Remarks"
                required={false}
                width="100%"
                placeholder="Enter Remarks"
                value={value}
                setChangeText={onChange}
              />
            )}
          />
          {errors.remark && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: false }}
            name="delay_reason"
            render={({ field: { onChange, value } }) => (
              <TextInputControl
                title="Delay Reason"
                required={false}
                width="100%"
                placeholder="Enter Delay Reason"
                value={value}
                setChangeText={onChange}
              />
            )}
          />
          {errors.delay_reason && <Text style={styles.errorColor}>* This is required.</Text>}

          <Controller
            control={control}
            rules={{ required: false }}
            name="vessal_detail"
            render={({ field: { onChange, value } }) => (
              <TextInputControl
                title="Vessel Detail"
                required={false}
                width="100%"
                placeholder="Enter Vessel Detail"
                value={value}
                setChangeText={onChange}
              />
            )}
          />
          {errors.vessal_detail && <Text style={styles.errorColor}>* This is required.</Text>}

          {/* Submit */}
          {isUserAdmin && (
            <TouchableOpacity
              onPress={handleSubmit(onSubmit)}
              disabled={displayLoader}
              style={styles.submitButton}
            >
              {displayLoader ? (
                <Spinner size="small" color="#fff" />
              ) : (
                <Text style={styles.submitButtonText}>
                  {params?.mode === "create" ? "Create Container" : "Save Changes"}
                </Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      </Pressable>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: "#F1F5F9",
  },
  scrollContent: {
    paddingBottom: 48,
  },

  // ── Top Bar ──────────────────────────────────────────
  topBar: {
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "#F1F5F9",
    alignItems: "center",
    justifyContent: "center",
  },
  topBarCenter: {
    flex: 1,
    backgroundColor: "transparent",
  },
  topBarTitle: {
    fontSize: 22,
    fontWeight: "700",
    color: "#111827",
    letterSpacing: 0.2,
  },
  topBarSubtitle: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 2,
  },

  // ── Container wrapper ─────────────────────────────────
  container: {
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    minHeight: "100%" as any,
    backgroundColor: "#F1F5F9",
    paddingTop: 16,
  },

  // ── Upload section ────────────────────────────────────
  section: {
    width: "93%",
    marginBottom: 8,
    backgroundColor: "transparent",
  },
  uploadBox: {
    backgroundColor: "#FFFFFF",
    borderWidth: 2,
    borderStyle: "dashed" as any,
    borderColor: "#BFDBFE",
    borderRadius: 14,
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    marginBottom: 20,
  },
  uploadTitle: {
    color: "#2F95DC",
    fontWeight: "700",
    fontSize: 14,
    marginTop: 4,
  },
  uploadSub: {
    color: "#94A3B8",
    fontSize: 11,
  },
  dividerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
    backgroundColor: "transparent",
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: "#E2E8F0",
  },
  dividerText: {
    marginHorizontal: 12,
    color: "#94A3B8",
    fontSize: 11,
    fontWeight: "600",
  },

  // ── Form Card ─────────────────────────────────────────
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E8EDF5",
    shadowColor: "#1E293B",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.07,
    shadowRadius: 10,
    elevation: 3,
    maxWidth: 800,
    width: "93%",
    alignSelf: "center",
  },

  // ── Section headers inside the card ──────────────────
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#F8FAFC",
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 8,
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: "700",
    color: "#2F95DC",
    letterSpacing: 0.8,
  },
  cardDivider: {
    height: 1,
    backgroundColor: "#F1F5F9",
    marginVertical: 18,
  },

  // ── Submit ────────────────────────────────────────────
  submitButton: {
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    backgroundColor: "#2F95DC",
    borderRadius: 12,
    marginTop: 24,
    width: "100%",
    shadowColor: "#2F95DC",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 4,
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontWeight: "700",
    fontSize: 15,
    letterSpacing: 0.3,
  },

  // ── Validation ────────────────────────────────────────
  errorColor: {
    color: "#EF4444",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 4,
  },
  successColor: {
    color: "#10B981",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 4,
  },
});
